import {
  Button,
  EllipsisSpan,
  Table,
  Wrapper,
  useSearchParams,
  useUpdate,
  useBreadcrumbRoutes,
  Paging,
  CustomFilter,
  Select,
  Tag,
  Dropdown,
  Modal,
  Input,
} from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { Form, Space } from 'antd';
import { Modal as AntModal } from 'antd';
import {
  apiV2GgdAddRemarkPost,
  apiV2GgdBatchOperateStatusPost,
  apiV2GgdDeviceListPost,
  apiV2GgdDevicePagePost,
  apiV2GgdDistributeTimeTaskPost,
  V2GgdDevicePagePostResponse,
  V2OutputProductBaseLinePagePostRequest,
} from '@maxtropy/device-customer-apis-v2';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router';
import { Key, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { DistributeStatus, statusOptions, StatusType } from './utils';
import ScheduledTaskModal from './components/ScheduledTaskModal';
import ApplyLogModal from './components/ApplyLogModal';
import { isNil } from 'lodash-es';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useSearchParams as useRouteSearchParams } from 'react-router-dom';
import { set } from 'video.js/dist/types/tech/middleware';

export type LightItem = Exclude<V2GgdDevicePagePostResponse['list'], undefined>[number];

interface SearchParamsProps {
  deviceId?: number;
  status?: number | string;
}

const columns: ColumnsType<LightItem> = [
  {
    title: '控制设备名称',
    dataIndex: 'deviceName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '闸位状态',
    dataIndex: 'status',
    ellipsis: { showTitle: true },
    render: (v: number) =>
      isNil(v) ? (
        '--'
      ) : v === StatusType.OPEN ? (
        <Tag border="solid" color="default">
          分闸
        </Tag>
      ) : (
        <Tag border="solid" color="success">
          合闸
        </Tag>
      ),
  },
  {
    title: '闸位命令最新下发状态',
    dataIndex: 'operateDistributeStatus',
    ellipsis: { showTitle: true },
    render: (v: number) =>
      isNil(v) ? (
        '--'
      ) : v === DistributeStatus.PENDING ? (
        <Tag border="solid" color="warning">
          下发中
        </Tag>
      ) : v === DistributeStatus.SUCCESS ? (
        <Tag border="solid" color="success">
          下发成功
        </Tag>
      ) : v === DistributeStatus.FINISH ? (
        <Tag border="solid" color="info">
          已下发
        </Tag>
      ) : (
        <Tag border="solid" color="error">
          下发失败
        </Tag>
      ),
  },
  {
    title: '闸位命令最新下发时间',
    dataIndex: 'operateDistributeTime',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={isNil(v) ? '--' : dayjs(v).format('YYYY-MM-DD HH:mm:ss')} />,
  },
  {
    title: '最后操作人',
    dataIndex: 'updateByUserName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '最后操作时间',
    dataIndex: 'updateTime',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={isNil(v) ? '--' : dayjs(v).format('YYYY-MM-DD HH:mm:ss')} />,
  },
];

interface SearchParams extends Omit<V2OutputProductBaseLinePagePostRequest, 'page' | 'size'> {}

const PowerLightingControl: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const pagingInfo = useSearchParams<SearchParams>(50);
  const [searchParams, setSearchParams] = useState<SearchParamsProps>({ status: 9999 });
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [update, updateFn] = useUpdate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [showScheduledTaskModal, setShowScheduledTaskModal] = useState<boolean>(false);
  const [showApplyLogModal, setShowApplyLogModal] = useState<boolean>(false);
  const [currentDeviceId, setCurrentDeviceId] = useState<number>();
  const [currentId, setCurrentId] = useState<number>();
  const [batchBtnVisible, setBatchBtnVisible] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [batchStatus, setBatchStatus] = useState<StatusType>(StatusType.OPEN);
  const [batchLoading, setBatchLoading] = useState<boolean>(false);
  const [urlSearchParams] = useRouteSearchParams();
  const url_deviceId = urlSearchParams.get('deviceId') ? Number(urlSearchParams.get('deviceId')) : undefined;
  const [modalApi, modalContextHolder] = AntModal.useModal();
  const [showRemarkModal, setShowRemarkModal] = useState<boolean>(false);
  const [remark, setRemark] = useState<string>('');
  const { data, loading } = useRequest(
    () =>
      apiV2GgdDevicePagePost({
        ...searchParams,
        status: searchParams.status === 9999 ? undefined : searchParams.status,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res.total ?? 0);
        return res.list;
      }),
    {
      refreshDeps: [searchParams, pageOffset, pageSize, update],
    }
  );

  const { data: deviceList } = useRequest(async () => {
    const res = await apiV2GgdDeviceListPost({});
    return res.list;
  });

  const rowSelection = {
    preserveSelectedRowKeys: true,
    onChange: (rowKeys: Key[]) => {
      setSelectedRows(rowKeys as number[]);
    },
    selectedRowKeys: selectedRows,
    getCheckboxProps: (rows: any) => ({
      disabled: batchStatus === rows.status,
    }),
  };

  const deviceOptions = useMemo(() => {
    return (deviceList ?? []).map(item => ({
      label: item.deviceName,
      value: item.deviceId,
    }));
  }, [deviceList]);

  useEffect(() => {
    if (url_deviceId && deviceOptions.length) {
      form.setFieldsValue({
        deviceId: url_deviceId,
      });
      const params = {
        deviceId: url_deviceId,
        status: 9999,
      };
      setSearchParams(params);
    }
  }, [url_deviceId, deviceOptions]);

  const makeLoadingModel = () =>
    modalApi.info({
      className: 'loading-modal',
      title: '正在执行',
      content: (
        <div className="loading-modal-content">
          <div className="loading-modal-icon">
            <LoadingOutlined />
          </div>
          <div className="loading-modal-txt">正在执行中</div>
        </div>
      ),
    });

  const remoteControl = (deviceIds: number[], deviceNames: string[], status: StatusType) => {
    if (!deviceIds.length) return;
    Modal.confirm({
      title: (
        <span style={{ color: 'var(--mx-text-base-color)' }}>
          确定要下发<span style={{ color: '#E64242' }}>{status === StatusType.CLOSED ? '远程合闸' : '远程分闸'}</span>
          指令到{deviceNames.join('、')}设备吗？
        </span>
      ),
      okText: '确定',

      onOk() {
        const modal = makeLoadingModel();
        setBatchBtnVisible(false);
        setSelectedRows([]);
        apiV2GgdBatchOperateStatusPost({ deviceIds, status })
          .then(_ => {
            updateFn();
          })
          .finally(() => {
            modal.destroy();
          });
      },
    });
  };

  const distributeTask = (id: number) => {
    if (isNil(id)) return;
    apiV2GgdDistributeTimeTaskPost({
      id: id!,
    }).then(res => {
      updateFn();
    });
  };

  const onFinish = (v: SearchParams) => {
    setSearchParams({ ...v });
    setPageOffset(1);
  };

  const onReset = () => {
    const params = {
      deviceId: undefined,
      status: 9999,
    };
    setSearchParams(params);
    setPageOffset(1);
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      dataIndex: 'operation',
      width: 200,
      fixed: 'right' as 'right',
      render: (value: any, record: any) => {
        return (
          <Space size={16}>
            <Button
              type="link"
              onClick={() => {
                setCurrentId(record.id);
                setRemark(record?.locationRemark ?? '');
                setShowRemarkModal(true);
              }}
            >
              添加编辑位置备注
            </Button>
            <Button
              type="link"
              onClick={() => {
                window.open(`/data/history/device?deviceId=${record?.deviceId}`, '_blank');
              }}
            >
              查看详情
            </Button>

            <Dropdown
              type="link"
              menu={{
                items: [
                  {
                    key: '1',
                    label: '远程分闸',
                    disabled:
                      isNil(record.status) || record.status === StatusType.OPEN || record.status === StatusType.PENDING,
                    onClick: () => {
                      remoteControl([record.deviceId], [record.deviceName], StatusType.OPEN);
                    },
                  },
                  {
                    key: '2',
                    label: '远程合闸',
                    disabled:
                      isNil(record.status) ||
                      record.status === StatusType.CLOSED ||
                      record.status === StatusType.PENDING,
                    onClick: () => {
                      remoteControl([record.deviceId], [record.deviceName], StatusType.CLOSED);
                    },
                  },
                  {
                    key: '3',
                    label: '设置定时任务',
                    disabled:
                      isNil(record.status) ||
                      !record.canSetTimeTask ||
                      record.taskDistributeStatus === DistributeStatus.PENDING ||
                      record.taskDistributeStatus === DistributeStatus.FINISH,
                    onClick: () => {
                      setCurrentId(record.id);
                      setCurrentDeviceId(record.deviceId);
                      setShowScheduledTaskModal(true);
                    },
                  },
                  {
                    key: '4',
                    label: '下发定时任务',
                    disabled:
                      isNil(record.status) ||
                      !record.canSetTimeTask ||
                      record.taskDistributeStatus === DistributeStatus.PENDING ||
                      record.taskDistributeStatus === DistributeStatus.FINISH,
                    onClick: () => {
                      distributeTask(record.id);
                    },
                  },
                  {
                    key: '5',
                    label: '下发记录',
                    onClick: () => {
                      setCurrentId(record.id);
                      setShowApplyLogModal(true);
                    },
                  },
                ],
              }}
            >
              控制
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const filters = (
    <CustomFilter form={form} onFinish={onFinish} onReset={onReset} initialValues={{ status: 9999 }}>
      <Form.Item label="控制设备名称" name="deviceId">
        <Select placeholder="请选择" optionFilterProp="label" showSearch options={deviceOptions} />
      </Form.Item>
      <Form.Item label="闸位状态" name="status">
        <Select options={statusOptions} />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper filters={filters} routes={[...(breadcrumbRoutes?.routes ?? [])]}>
      {batchBtnVisible ? (
        <Space size={8} style={{ marginBottom: 10 }}>
          <Button
            type="primary"
            onClick={() => {
              remoteControl(
                selectedRows,
                deviceList?.filter(i => selectedRows.includes(i.deviceId!)).map(i => i.deviceName!) || [],
                batchStatus
              );
            }}
          >
            {batchStatus === StatusType.CLOSED ? '确认合闸' : '确认分闸'}
          </Button>
          <Button
            onClick={() => {
              setBatchBtnVisible(false);
              setSelectedRows([]);
              setBatchStatus(StatusType.OPEN);
            }}
          >
            取消
          </Button>
          <InfoCircleOutlined style={{ color: 'var(--warning-color)', marginLeft: 6 }} />
          请选择控制设备后点击【{batchStatus === StatusType.CLOSED ? '确认合闸' : '确认分闸'}】按钮，已选择
          <span style={{ color: '#E64242' }}>{selectedRows.length}</span>
          设备
        </Space>
      ) : (
        <Space style={{ marginBottom: 10 }}>
          <Button
            type="primary"
            onClick={() => {
              navigate(`/power/lighting/control/log`);
              //   setCurrentId(undefined);
              //   setCurrentDeviceId(undefined);
              //   setShowScheduledTaskModal(true);
            }}
          >
            操作日志
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setCurrentId(undefined);
              setCurrentDeviceId(undefined);
              setShowScheduledTaskModal(true);
            }}
          >
            批量设置定时任务
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setBatchBtnVisible(true);
              setSelectedRows([]);
              setBatchStatus(StatusType.CLOSED);
            }}
          >
            批量合闸
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setBatchBtnVisible(true);
              setSelectedRows([]);
              setBatchStatus(StatusType.OPEN);
            }}
          >
            批量分闸
          </Button>
        </Space>
      )}

      <Table
        loading={loading}
        rowKey="deviceId"
        columns={buildColumns}
        dataSource={data}
        rowSelection={
          batchBtnVisible
            ? {
                type: 'checkbox',
                ...rowSelection,
              }
            : undefined
        }
      />
      <Paging pagingInfo={pagingInfo} />
      {showScheduledTaskModal && (
        <ScheduledTaskModal
          id={currentId}
          deviceId={currentDeviceId}
          open={showScheduledTaskModal}
          setOpen={setShowScheduledTaskModal}
          updateFn={updateFn}
        />
      )}
      {showApplyLogModal && (
        <ApplyLogModal
          id={currentId}
          visible={showApplyLogModal}
          onCancel={() => {
            setShowApplyLogModal(false);
          }}
        ></ApplyLogModal>
      )}
      {modalContextHolder}
      {showRemarkModal && (
        <Modal
          size="small"
          title="添加/编辑位置备注"
          open={showRemarkModal}
          onCancel={() => setShowRemarkModal(false)}
          onOk={() => {
            // 处理提交逻辑，例如调用 API 更新备注
            console.log('currentId :>> ', currentId);
            apiV2GgdAddRemarkPost({
              id: currentId!,
              remark: remark,
            }).then(() => {
              console.log('提交备注:', remark);
              updateFn();
              setShowRemarkModal(false);
            });
          }}
        >
          <Form.Item label="备注">
            <Input value={remark} onChange={e => setRemark(e.target.value)} placeholder="请输入备注" maxLength={20} />
          </Form.Item>
        </Modal>
      )}
    </Wrapper>
  );
};

export default PowerLightingControl;
